import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pill, Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const BLANK_MED = { name: "", dosage: "", route: "PO", instructions: "", scheduled_times: "", is_prn: false };
const ROUTES = ["PO", "topical", "IM", "SC", "IV", "INH", "PR", "SL"];

/** Medication CRUD section for a single client (inside the edit modal). */
export default function ClientMedications({ clientId, medications, setMedications }) {
  const [form, setForm] = useState(BLANK_MED);
  const [editingMedId, setEditingMedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setForm(BLANK_MED);
    setEditingMedId(null);
  };

  const save = async () => {
    if (!clientId) return;
    if (!form.name.trim() || !form.dosage.trim()) {
      toast.error("Name and dosage are required");
      return;
    }
    const times = form.scheduled_times.split(",").map((t) => t.trim()).filter(Boolean);
    if (!form.is_prn && times.length === 0) {
      toast.error("Add at least one scheduled time (HH:MM), or mark as PRN");
      return;
    }
    const bad = times.find((t) => !/^\d{1,2}:\d{2}$/.test(t));
    if (bad) {
      toast.error(`Invalid time "${bad}". Use HH:MM`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        dosage: form.dosage.trim(),
        route: form.route,
        instructions: form.instructions.trim(),
        scheduled_times: times,
        is_prn: form.is_prn,
      };
      if (editingMedId) {
        const { data } = await api.put(`/medications/${editingMedId}`, payload);
        setMedications((arr) => arr.map((m) => (m.id === editingMedId ? data : m)));
        toast.success("Medication updated");
      } else {
        const { data } = await api.post(`/clients/${clientId}/medications`, payload);
        setMedications((arr) => [...arr, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Medication added");
      }
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (m) => {
    setEditingMedId(m.id);
    setForm({
      name: m.name,
      dosage: m.dosage,
      route: m.route || "PO",
      instructions: m.instructions || "",
      scheduled_times: (m.scheduled_times || []).join(", "),
      is_prn: !!m.is_prn,
    });
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}? This won't affect past administration records.`)) return;
    try {
      await api.delete(`/medications/${m.id}`);
      setMedications((arr) => arr.filter((x) => x.id !== m.id));
      toast.success("Medication removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="border-t border-clinical-border pt-4">
      <div className="flex items-center gap-2 mb-2">
        <Pill className="h-4 w-4 text-clinical-primary" />
        <span className="label-eyebrow !text-clinical-primary">Medications</span>
      </div>
      <p className="text-xs text-clinical-subtext mb-3">
        Scheduled medications drive the daily MAR. Times are 24-hour, comma-separated
        (e.g. <span className="font-mono">08:00, 20:00</span>). Mark PRN for as-needed.
      </p>

      {medications.length > 0 && (
        <ul className="space-y-1.5 mb-3" data-testid="medications-list">
          {medications.map((m) => (
            <li
              key={m.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-clinical-border p-2.5"
              data-testid={`medication-${m.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">
                  {m.name}{" "}
                  <span className="font-normal text-clinical-subtext">· {m.dosage}</span>
                  {m.route !== "PO" && (
                    <span className="ml-1.5 pill bg-clinical-bg text-clinical-subtext">{m.route}</span>
                  )}
                  {m.is_prn && (
                    <span className="ml-1.5 pill bg-clinical-accent/10 text-clinical-accent">PRN</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-clinical-subtext">
                  {m.scheduled_times?.length > 0
                    ? m.scheduled_times.join(" · ")
                    : m.is_prn
                    ? "As needed"
                    : "—"}
                  {m.instructions && ` · ${m.instructions}`}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => startEdit(m)} className="btn-ghost p-1.5"
                  data-testid={`edit-med-${m.id}`} title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => remove(m)} className="btn-ghost p-1.5 hover:!text-destructive"
                  data-testid={`remove-med-${m.id}`} title="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-dashed border-clinical-border p-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Medication (e.g. Metformin)" className="input-clinical" data-testid="med-name-input" />
          <input value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })}
            placeholder="Dosage (e.g. 500 mg)" className="input-clinical" data-testid="med-dosage-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })}
            className="input-clinical" data-testid="med-route-select">
            {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input value={form.scheduled_times} onChange={(e) => setForm({ ...form, scheduled_times: e.target.value })}
            placeholder="Times (08:00, 20:00)" className="input-clinical sm:col-span-2"
            disabled={form.is_prn} data-testid="med-times-input" />
        </div>
        <input value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
          placeholder="Instructions (optional)" className="input-clinical" data-testid="med-instructions-input" />
        <div className="flex items-center justify-between gap-2 pt-1">
          <label className="flex items-center gap-2 text-sm text-clinical-subtext cursor-pointer">
            <input type="checkbox" checked={form.is_prn}
              onChange={(e) => setForm({ ...form, is_prn: e.target.checked })}
              data-testid="med-prn-checkbox" className="accent-clinical-primary" />
            PRN (as needed)
          </label>
          <div className="flex items-center gap-2">
            {editingMedId && (
              <button type="button" onClick={resetForm} className="btn-secondary" data-testid="med-cancel">
                Cancel edit
              </button>
            )}
            <button type="button" onClick={save} disabled={saving} className="btn-primary" data-testid="med-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" />
                : editingMedId ? <>Save changes</>
                : (<><Plus className="h-4 w-4" /> Add medication</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
